import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'react-bootstrap-icons';
import useLocalization from '../../hooks/useLocalization';
import {
  Archive,
  Box,
  CircleCheck,
  CircleDotDashed,
  Clipboard,
  CopyCheck,
  Pencil,
  Trash2,
  Upload,
  Star,
  GripVertical
} from 'lucide-react';
import NewDropDown from './NewDropDown';
import { Button as ButtonTW } from '../ui/button';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FormCheck } from 'react-bootstrap';

const Tabs = ({
  tabs = [],
  tabsType,
  labelKey = 'name',
  valueKey = '_id',
  activeTab,
  selectedTab,
  onTabClick,
  onEditClick,
  onDeleteClick,
  onCloneClick,
  onMoveToSandboxClick,
  onMoveToMainboxClick,
  onKillClick,
  onSelectClick,
  disabled,
  scrollable,
  readOnlyMode,
  activeTabVariant = 'primary',
  fromCustomerPortal,
  enableStrategiesRearranging,
  onReorder,
  getTabVariantColor,
  getTabTextColor,
  multiSelectMode = false,
  checkedTabIds = [],
  onMarkSeveralClick,
  onCheckedChange
}) => {
  const { translate, isRTL } = useLocalization();
  const tabsContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [localTabs, setLocalTabs] = useState(tabs);

  useEffect(() => {
    setLocalTabs(tabs);
  }, [tabs]);

  const displayTabs = useMemo(() => {
    return isRTL ? [...localTabs].reverse() : localTabs;
  }, [localTabs, isRTL]);

  const tabsContainerStyle = useMemo(
    () => ({
      ...(scrollable
        ? {
            whiteSpace: 'nowrap',
            scrollbarWidth: 'none',
            overflowY: 'visible !important',
            overflowX: 'auto'
          }
        : {}),
      display: 'flex',
      alignItems: 'center',
      opacity: disabled ? 0.9 : 1,
      // dnd don't support rtl drag and drop.
      // So, we need to set direction: ltr; and reverse the tabs order for rtl.
      // also justify-content: flex-end; but it disable scroll behavious in rtl so, we have to used hacker to push content to the right for rtl.
      direction: 'ltr',
      justifyContent:'flex-start'
    }),
    [scrollable, disabled]
  );

  useEffect(() => {
    const THRESHOLD = 10;
    const container = tabsContainerRef.current;
    if (!container) return;

    function updateScrollArrows() {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const scrollLeftMax = scrollWidth - clientWidth;
      setCanScrollLeft(scrollLeft > THRESHOLD);
      setCanScrollRight(scrollLeft < scrollLeftMax - THRESHOLD);
    }

    updateScrollArrows();
    container.addEventListener('scroll', updateScrollArrows);
    window.addEventListener('resize', updateScrollArrows);
    return () => {
      container.removeEventListener('scroll', updateScrollArrows);
      window.removeEventListener('resize', updateScrollArrows);
    };
  }, [isRTL, localTabs]);

  const handleScrollClick = direction => {
    const container = tabsContainerRef.current;
    const scrollAmount = container.clientWidth / 2;
    container.scroll({
      left: direction === 'left' ? container.scrollLeft - scrollAmount : container.scrollLeft + scrollAmount,
      behavior: 'smooth'
    });
  };

  const hasActions = useMemo(
    () =>
      (onEditClick ||
        onDeleteClick ||
        onCloneClick ||
        onKillClick ||
        onSelectClick ||
        onMoveToSandboxClick ||
        onMoveToMainboxClick) &&
      !readOnlyMode,
    [
      onEditClick,
      onDeleteClick,
      onCloneClick,
      onKillClick,
      onSelectClick,
      readOnlyMode,
      onMoveToMainboxClick,
      onMoveToSandboxClick
    ]
  );

  const getDropDownItems = (
    tabsType,
    onEditClick,
    onDeleteClick,
    onCloneClick,
    onKillClick,
    onSelectClick,
    onMoveToSandboxClick,
    onMoveToMainboxClick,
    value
  ) => {
    const hasEditOrDeleteAccess = onEditClick || onDeleteClick;
    const items = [];

    if (onSelectClick) {
      items.push({
        onClick: () => onSelectClick(value),
        text: selectedTab === value ? translate('unselect') : translate('select'),
        icon: selectedTab === value ? <CircleDotDashed size={13} /> : <CircleCheck size={13} />
      });
    }

    if (onMarkSeveralClick) {
      items.push({
        onClick: () => onMarkSeveralClick(value),
        text: translate('mark_several_strategies'),
        icon: <CopyCheck size={13} />
      });
    }

    if (onCloneClick) {
      items.push({ onClick: () => onCloneClick(value), text: translate('clone'), icon: <Clipboard size={13} /> });
    }

    if (onEditClick) {
      items.push({ onClick: () => onEditClick(value), text: translate('edit'), icon: <Pencil size={13} /> });
    }

    if (onKillClick && hasEditOrDeleteAccess) {
      items.push({
        onClick: () => onKillClick(value),
        text: translate('archive'),
        icon: <Archive size={13} />
      });
    }
    if (!fromCustomerPortal && (onMoveToMainboxClick || onMoveToSandboxClick)) {
      items.push({
        onClick: () => (tabsType === 'sandbox' ? onMoveToMainboxClick(value) : onMoveToSandboxClick(value)),
        text: tabsType === 'sandbox' ? translate('move_to_mainbox') : translate('move_to_sandbox'),
        icon: tabsType === 'sandbox' ? <Upload size={13} /> : <Box size={13} />
      });
    }
    if (onDeleteClick) {
      items.push({
        onClick: () => onDeleteClick(value),
        text: translate('delete'),
        icon: <Trash2 size={13} className="text-red-600" />,
        className: 'text-red-600 hover:bg-red-50'
      });
    }
    return items;
  };

  const renderTabContent = (tab, label, value) => {
    const isActive = activeTab === value;
    const isChecked = checkedTabIds.includes(value);
    const roundedClass = isRTL ? 'rounded-l-md rounded-r-none' : 'rounded-r-md rounded-l-none';

    const commonButtonProps = {
      className: `relative text-sm px-[20px] py-[30px] m-0 border-0 transition-all duration-300 ${roundedClass}`,
      onClick: () => {
        if (multiSelectMode) {
          onCheckedChange?.(value);
          return;
        }
        onTabClick(value);
      },
      disabled: disabled,
      variant: 'mutedBlue',
      style: { color: 'inherit' }
    };

    const starOverlay = !multiSelectMode && selectedTab === value && (
      <div
        className={`flex items-center justify-center absolute top-1 ${
          isRTL ? 'left-1' : 'right-1'
        } w-5 h-5 rounded-full text-gray bg-white`}
      >
        <Star size={15} strokeWidth={0} fill="#fdb915" />
      </div>
    );

    if (multiSelectMode) {
      return (
        <ButtonTW
          {...commonButtonProps}
          className={`${commonButtonProps.className} ${
            isChecked ? 'font-medium bg-blue-300 hover:bg-blue-300' : 'font-normal'
          }`}
        >
          <span className="flex items-center gap-2">
            <FormCheck
              type="checkbox"
              checked={isChecked}
              onChange={() => onCheckedChange?.(value)}
              onClick={e => e.stopPropagation()}
              className="m-0"
            />
            <span>{label}</span>
          </span>
        </ButtonTW>
      );
    }

    return (
      <>
        {hasActions && isActive ? (
          <NewDropDown
            trigger={
              <ButtonTW
                {...commonButtonProps}
                className={`${commonButtonProps.className} font-medium bg-blue-300 hover:bg-blue-300`}
              >
                {starOverlay}
                {label}
              </ButtonTW>
            }
            items={getDropDownItems(
              tabsType,
              onEditClick,
              onDeleteClick,
              onCloneClick,
              onKillClick,
              onSelectClick,
              onMoveToSandboxClick,
              onMoveToMainboxClick,
              value
            )}
          />
        ) : (
          <ButtonTW
            {...commonButtonProps}
            className={`${commonButtonProps.className} ${
              isActive ? 'font-medium bg-blue-300 hover:bg-blue-300' : 'font-normal'
            }`}
          >
            {starOverlay}
            {label}
          </ButtonTW>
        )}
      </>
    );
  };

  const onDragEnd = result => {
    if (!result.destination) return;
    let sourceIndex = result.source.index;
    let destinationIndex = result.destination.index;

    if (isRTL) {
      sourceIndex = localTabs.length - 1 - sourceIndex;
      destinationIndex = localTabs.length - 1 - destinationIndex;
    }

    const items = Array.from(localTabs);
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);
    setLocalTabs(items);
    onReorder && onReorder(items);
  };

  return (
    <div style={{ position: 'relative' }}>
      {scrollable && canScrollLeft && (
        <ButtonTW
          size="sm"
          variant="default"
          className="rounded-none rounded-l h-full px-1 m-0"
          style={{ position: 'absolute', top: '50%', left: 0, transform: 'translateY(-50%)', zIndex: 10 }}
          onClick={() => handleScrollClick('left')}
        >
          <ArrowLeft />
        </ButtonTW>
      )}
      <div style={{ overflow: 'visible' }}>
        {enableStrategiesRearranging ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={tabsType || 'tabs'} direction="horizontal">
              {provided => (
                <div
                  ref={el => {
                    tabsContainerRef.current = el;
                    provided.innerRef(el);
                  }}
                  {...provided.droppableProps}
                  style={tabsContainerStyle}
                >
                  {/* HACK: Pushes items to the right in RTL while staying in LTR mode */}
                  {isRTL && <div style={{ flexGrow: 1 }} />}
                  {displayTabs.map((tab, index) => {
                    const isObject = typeof tab === 'object';
                    const label = isObject ? tab[labelKey] : tab;
                    const value = isObject ? tab[valueKey] : tab;
                    const isDraggableItem = !(isObject && tab.isDefaultStrategy);
                    const barColor = getTabVariantColor(tab) || '#b2e0fe';
                    const textColor = getTabTextColor(tab);
                    const barRoundedClass = isRTL ? 'rounded-r-md rounded-l-none' : 'rounded-l-md rounded-r-none';

                    return (
                      <Draggable
                        key={String(value)}
                        draggableId={String(value)}
                        index={index}
                        isDragDisabled={!isDraggableItem || disabled}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className="d-inline-flex align-items-stretch me-3"
                            style={{
                              ...provided.draggableProps.style,
                              color: textColor,
                              opacity: snapshot.isDragging ? 0.8 : 1,
                              zIndex: snapshot.isDragging ? 9999 : 'auto',
                              backgroundColor: 'transparent',
                              direction: isRTL ? 'rtl' : 'inherit'
                            }}
                          >
                            <div
                              className={`flex items-center justify-center w-[12px] ${barRoundedClass}`}
                              style={{ backgroundColor: barColor }}
                            >
                              {isDraggableItem && (
                                <div
                                  {...provided.dragHandleProps}
                                  className={`text-white hover:text-white ${
                                    disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'
                                  }`}
                                >
                                  <GripVertical size={14} />
                                </div>
                              )}
                            </div>
                            {renderTabContent(tab, label, value)}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div ref={tabsContainerRef} style={tabsContainerStyle}>
            {/* HACK: Pushes items to the right in RTL while staying in LTR mode */}
            {isRTL && <div style={{ flexGrow: 1 }} />}
            {displayTabs.map(tab => {
              const isObject = typeof tab === 'object';
              const label = isObject ? tab[labelKey] : tab;
              const value = isObject ? tab[valueKey] : tab;
              const barColor = getTabVariantColor(tab) || '#b2e0fe';
              const textColor = getTabTextColor(tab);
              const barRoundedClass = isRTL ? 'rounded-r-md rounded-l-none' : 'rounded-l-md rounded-r-none';

              return (
                <div
                  key={String(value)}
                  className="d-inline-flex align-items-stretch me-3"
                  style={{ color: textColor, direction: isRTL ? 'rtl' : 'inherit' }}
                >
                  <div className={`w-[12px] ${barRoundedClass}`} style={{ backgroundColor: barColor }} />
                  {renderTabContent(tab, label, value)}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {scrollable && canScrollRight && (
        <ButtonTW
          size="sm"
          variant="default"
          className="rounded-none rounded-r px-1 h-full m-0"
          style={{ position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)', zIndex: 10 }}
          onClick={() => handleScrollClick('right')}
        >
          <ArrowRight />
        </ButtonTW>
      )}
    </div>
  );
};

export default Tabs;
